import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip } from '@mui/material';
import { Add, Edit, Visibility, Delete, FileDownload } from '@mui/icons-material';
import AddEmployeeModal from './AddEmployeeModal';
import UpdateEmployeeModal from './UpdateEmployeeModal';
import ViewEmployeeModal from './ViewEmployeeModal';
import api from '../config/backend';
import './css/Employee.css';
import DownloadExportData from '../utils/excel';

function Employee() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const token = localStorage.getItem("_crm__token");

  // Define columns for DataGrid
  const columns = [
    { field: 'id', headerName: 'S No', width: 70 },
    { field: 'firstName', headerName: 'First Name', width: 130 },
    { field: 'lastName', headerName: 'Last Name', width: 130 },
    { field: 'company_email', headerName: 'Company Email', width: 200 },
    { field: 'Designation', headerName: 'Designation', width: 150 },
    // { field: 'Employment_type', headerName: 'Employment Type', width: 150 },
    { field: 'mobile_no', headerName: 'Mobile', width: 120 },
    {
      field: 'Status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <span className={`status-badge ${params.value ? 'active' : 'inactive'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <div className="action-buttons">
          <Tooltip title="View Details">
            <IconButton
              onClick={() => handleViewEmployee(params.row._id)}
              size="small"
              className="view-btn"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => handleEditEmployee(params.row._id)}
              size="small"
              className="edit-btn"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}/Employee/get`);
      if (response.data.success) {
        const formattedEmployees = response.data.employees.map((emp, index) => ({
          ...emp,
          id: index + 1
        }));
        setEmployees(formattedEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmployee = async (id) => {
    try {
      const response = await axios.post(`${api}/Employee/getById`, {
        token,
        id
      });

      if (response.data.success) {
        setSelectedEmployee(response.data.Data);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
  };

  const handleEditEmployee = async (id) => {
    try {
      const response = await axios.post(`${api}/Employee/getById`, {
        token,
        id
      });

      if (response.data.success) {
        setSelectedEmployee(response.data.Data);
        setShowUpdateModal(true);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
    }
  };

  const handleAddSuccess = () => {
    fetchEmployees();
    setShowAddModal(false);
  };

  const handleUpdateSuccess = () => {
    fetchEmployees();
    setShowUpdateModal(false);
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      // Using axios to download the file
      const response = await axios({
        url: `${api}/Employee/get?download=true`,
        method: 'GET',
        responseType: 'blob', // Important for file downloads
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      DownloadExportData(response.data, 'Employee.csv');

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="employee-management-container">
      <div className="employee-header">
        <h2>Employee Management</h2>
        <div className="employee-actions">
          <Button
            variant="outlined"
            endIcon={<FileDownload />}
            onClick={handleDownloadExcel}
            // className="download-excel-btn"
            disabled={downloading || employees.length === 0}
          >
            {/* {downloading ? 'Downloading...' : 'Export to Excel'} */}
            <img src='https://cdn-icons-png.flaticon.com/512/888/888850.png' alt='excel_image' width={'30px'} height={'30px'} />
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowAddModal(true)}
            className="add-employee-btn"
          >
            Add Employee
          </Button>
        </div>
      </div>

      <div className="app-table">
        <DataGrid
          rows={employees}
          columns={columns}
          loading={loading}
          pageSize={10}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10, 20]}
          // rowsPerPageOptions={[5, 10, 20]}
          // disableSelectionOnClick
          // autoHeight
        />
      </div>

      {/* Add Employee Modal */}
      {showAddModal && (
        <AddEmployeeModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          token={token}
        />
      )}

      {/* Update Employee Modal */}
      {showUpdateModal && selectedEmployee && (
        <UpdateEmployeeModal
          open={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          onSuccess={handleUpdateSuccess}
          employee={selectedEmployee}
          token={token}
        />
      )}

      {/* View Employee Modal */}
      {showViewModal && selectedEmployee && (
        <ViewEmployeeModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          employee={selectedEmployee}
        />
      )}
    </div>
  );
}

export default Employee;
